import HttpService from '@/common/services/HttpService'
import {
  type FieldDTOV1,
  type CustomFieldDTOV1,
  type FieldContextEnum,
  EntityFieldCodeEnum,
  CUSTOMFIELD_CODE_PREFIX,
  FieldTypeEnum,
  type Component,
} from '@/common/types/fields'
import { PrefixTranslationsEnum, SuffixTranslationsEnum } from '@/common/types/i18n'
import i18n from '@/plugins/i18n'
import { type MainEntity } from '@/common/types/general'
import localizationServerData from '@/common/services/serverData/localizationServerData'

const { t } = i18n.global

/**
 * List all available fields.
 * @param context The context where the field is applicable
 * @returns fields array
 */
export const listAllFields = async (context?: FieldContextEnum): Promise<FieldDTOV1[]> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const params: any = {}
  if (context) params.context = context
  const [fields] = await Promise.all([
    HttpService.getData<FieldDTOV1[]>('/api/v1/model/fields', {
      params,
    }),
    localizationServerData.fetch({
      prefixes: [PrefixTranslationsEnum.ENTITY_FIELD, PrefixTranslationsEnum.CUSTOMFIELD, PrefixTranslationsEnum.CFOPTION],
    }),
  ])
  return fields
}

/**
 * Retrieve a RefCustomField
 * @param code The id or code of the custom field
 * @returns CustomFieldDTOV1 type
 */
export const getCustomField = async (code: string): Promise<CustomFieldDTOV1> => {
  return HttpService.getData<CustomFieldDTOV1>(`/api/v1/model/fields/custom/${code}`)
}

/**
 * You have to load bundle fields before to use this method
 * @param code Field code
 * @returns field name
 */
export function getName(fieldData: FieldDTOV1): string {
  return getFieldTranslation(fieldData)
}

/**
 * You have to load bundle fields before to use this method
 * @param code Field code
 * @returns field short Name
 */
export function getShortName(fieldData: FieldDTOV1): string {
  return getFieldTranslation(fieldData, SuffixTranslationsEnum.SHORT)
}

/**
 * You have to load bundle fields before to use this method
 * @param code Field code
 * @returns field label
 */
export function getLabel(fieldData: FieldDTOV1): string {
  return getFieldTranslation(fieldData, SuffixTranslationsEnum.LABEL)
}

/**
 * @param code Field code
 * @param suffix translation suffix
 * @returns field translation
 */
export function getFieldTranslation(fieldData: FieldDTOV1, suffix: SuffixTranslationsEnum | '' = ''): string {
  if (fieldData.custom) {
    return t(PrefixTranslationsEnum.CUSTOMFIELD + fieldData.code.replace(CUSTOMFIELD_CODE_PREFIX, '') + suffix)
  }
  return t(PrefixTranslationsEnum.ENTITY_FIELD + fieldData.code + suffix)
}

export const getTrueLabel = (code: string) => {
  switch (code) {
    case EntityFieldCodeEnum.HAS_ATTACHMENTS:
    case EntityFieldCodeEnum.INTERACTION_HAS_ATTACHMENTS:
      return t('assist.dictionary.with')
    case EntityFieldCodeEnum.INTERACTION_SLA:
      return t('assist.dictionary.withinSLA')
    default:
      return t('assist.dictionary.yes')
  }
}

export const getFalseLabel = (code: string) => {
  switch (code) {
    case EntityFieldCodeEnum.HAS_ATTACHMENTS:
    case EntityFieldCodeEnum.INTERACTION_HAS_ATTACHMENTS:
      return t('assist.dictionary.without')
    case EntityFieldCodeEnum.INTERACTION_SLA:
      return t('assist.dictionary.outsideSLA')
    default:
      return t('assist.dictionary.no')
  }
}

export const isDate = (type: FieldTypeEnum | undefined): boolean => {
  return type ? [FieldTypeEnum.TIMESTAMP, FieldTypeEnum.LOCAL_DATE].includes(type) : false
}

/**
 * For CF we use the code, the property for CF is legacy now
 * @param field
 * @returns property used
 */
export const getFieldModelProperty = (field: FieldDTOV1): string => {
  return field.custom ? field.code : field.modelProperty!
}

export const flattenCustomFields = (entities: MainEntity[]) => {
  for (const entity of entities) {
    if (!entity.customFields?.length) continue

    for (const customFieldValue of entity.customFields) {
      entity[customFieldValue.customFieldCode] = customFieldValue.value
    }
  }
}

/**
 * Don't touch this method, we should accept the number 0 as value
 */
export const propertyValuePresent = (entity: MainEntity, property: string): boolean => {
  return (
    entity &&
    entity[property] !== null &&
    entity[property] !== undefined &&
    ((!Array.isArray(entity[property]) && entity[property] !== '') || (Array.isArray(entity[property]) && !!entity[property].length))
  )
}

export function getComponent<TComponent = Component>(
  code: string,
  type: FieldTypeEnum | null,
  SpecificFieldsComponentsMap: Map<string, TComponent>,
  FieldTypesComponentsMap: Map<FieldTypeEnum, TComponent>,
  defaultComponent?: TComponent,
) {
  let component = SpecificFieldsComponentsMap.get(code)
  if (component) return component

  if (type === null) return defaultComponent

  component = FieldTypesComponentsMap.get(type)
  if (component) return component

  return defaultComponent
}
